import type { TrackingDoc, TrackingEntry, TrackingKind } from '../../types/taActionBoard';
import { COLORS } from './format';

const KIND_LABEL: Record<TrackingKind, string> = {
  asked: 'Panelists asked',
  ready: 'Ready to schedule',
  offered: 'Slots offered to candidate',
  confirmed: 'Interview confirmed',
  blocked: 'Blocked',
  withdrawn: 'Offer withdrawn — holds released',
  'fr-proposed': 'Final round options proposed',
  'fr-confirmed': 'Final round confirmed by candidate',
  'fr-booked': 'Final round booked — invites out',
  'fr-debrief-done': 'Debrief done',
  'fr-withdrawn': 'Final round options withdrawn',
};

function statusBadgeStyle(status: string): { bg: string; text: string } {
  switch (status) {
    case 'confirmed':
    case 'final round confirmed':
    case 'final round booked':
      return { bg: '#DCF5E4', text: '#136C34' };
    case 'offered':
    case 'final round proposed':
      return { bg: COLORS.blueBg, text: COLORS.blueText };
    case 'ready to schedule':
      return { bg: COLORS.orangeBg, text: COLORS.orangeText };
    case 'blocked':
      return { bg: COLORS.redBg, text: COLORS.redText };
    default:
      return { bg: COLORS.cotton, text: '#484848' };
  }
}

export function StatusBadge({ status }: { status: string }) {
  const s = statusBadgeStyle(status);
  return (
    <span
      className="inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ backgroundColor: s.bg, color: s.text }}
    >
      {status}
    </span>
  );
}

/**
 * Which kinds belong to which section. One tracking DOCUMENT holds a candidate's
 * whole history, so the case section was showing final-round entries and the
 * final-round card was showing months-old case logs — TA's report was that the
 * final-round card is not the place to read about case scheduling.
 *
 * A kind not listed here is shown in every scope: a new kind should surface
 * somewhere rather than vanish until someone remembers this map.
 */
const SCOPE_KINDS: Record<'case' | 'final', TrackingKind[]> = {
  case: ['asked', 'ready', 'offered', 'confirmed', 'blocked', 'withdrawn'],
  final: ['fr-proposed', 'fr-confirmed', 'fr-booked', 'fr-debrief-done', 'fr-withdrawn'],
};

const OWNED_KINDS = new Set<string>([...SCOPE_KINDS.case, ...SCOPE_KINDS.final]);

/** 📞 SLOT TRACKING mini-table: timestamp | event | status badge. */
export function TrackingTable({
  tracking,
  scope,
}: {
  tracking: TrackingDoc | undefined;
  /** Show only this section's own entries. Omit for the full history. */
  scope?: 'case' | 'final';
}) {
  const all = (tracking?.entries || []).slice().reverse();
  const entries = scope
    ? all.filter((e) => SCOPE_KINDS[scope].includes(e.kind) || !OWNED_KINDS.has(e.kind))
    : all;
  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold tracking-wide text-gray-400">
        Slot tracking
      </div>
      {entries.length === 0 ? (
        <div className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-400">
          {all.length > 0 && scope
            ? `No entries for this round yet (${all.length} from other rounds).`
            : 'No tracking entries yet.'}
        </div>
      ) : (
        <table className="w-full text-xs">
          <tbody>
            {entries.map((e, i) => (
              <TrackingRow key={`${e.at}-${i}`} entry={e} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TrackingRow({ entry }: { entry: TrackingEntry }) {
  const at = new Date(entry.at);
  const ts = at.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  // several slots can be offered at once; older entries only carry `slot`
  const slotText = entry.slots?.length ? entry.slots.join(', ') : entry.slot || '';
  return (
    <tr className="border-b border-gray-100 last:border-0">
      <td className="whitespace-nowrap py-1.5 pr-3 text-gray-400">{ts}</td>
      <td className="py-1.5 pr-3 text-gray-700">
        {KIND_LABEL[entry.kind] || entry.kind}
        {slotText ? ` — ${slotText}` : ''}
        {entry.note ? <span className="text-gray-400"> · {entry.note}</span> : null}
      </td>
      <td className="py-1.5 text-right">
        <StatusBadge status={KIND_LABEL[entry.kind] ? statusOf(entry.kind) : entry.kind} />
      </td>
    </tr>
  );
}

const KIND_STATUS: Partial<Record<TrackingKind, string>> = {
  ready: 'ready to schedule',
  // withdrawing puts the candidate back in the queue
  withdrawn: 'ready to schedule',
  'fr-proposed': 'final round proposed',
  'fr-confirmed': 'final round confirmed',
  'fr-booked': 'final round booked',
  'fr-debrief-done': 'debrief done',
};

function statusOf(kind: TrackingKind): string {
  return KIND_STATUS[kind] ?? kind;
}
